import { useState } from "react";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { api } from "../lib/api";

const ARCHIVE_INBOX = "media@kickstartkenyafootball.com";
const ROLES = ["Football fan", "Press or media", "Coach or club official", "Player", "Family or friend", "Other"];
const EMPTY = {
  player_name: "",
  years: "",
  clubs: "",
  country: "",
  achievement: "",
  evidence: "",
  your_name: "",
  your_email: "",
  your_role: ROLES[0],
  notes: "",
};

const Field = ({ label, name, value, onChange, required, placeholder, span = "" }) => (
  <label className={`block ${span}`}>
    <span className="mono block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-400">
      {label}
      {required ? <span className="text-maasai"> *</span> : null}
    </span>
    <input
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      data-testid={`archive-${name.replace(/_/g, "-")}`}
      className="mt-2 w-full border border-white/20 bg-transparent px-3.5 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:border-ochre focus:outline-none"
    />
  </label>
);

/**
 * The archive is incomplete by definition, so it asks to be corrected.
 * `context` decides whether the copy talks about players or football firsts.
 */
export const MissingPlayer = ({ context = "player" }) => {
  const firsts = context === "first";
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/archive-submissions", form);
      toast.success("Thank you. We will research it and credit you if it lands.");
      setForm(EMPTY);
      setDone(true);
    } catch {
      toast.error("That didn't send. Email us instead and we will pick it up.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border-t border-white/10 bg-[#0b0b0b]" data-testid="archive-callout">
      <div className="mx-auto max-w-[1500px] px-5 py-16 md:px-10 md:py-20">
        <p className="eyebrow">Help us finish the record</p>
        <h2 className="display-2 mt-4 max-w-4xl">
          {firsts ? "Know A First We Have Missed?" : "Know A Player We Have Missed?"}
        </h2>
        <p className="body-copy mt-6 max-w-3xl">
          {firsts
            ? "This timeline is only as complete as the records we could find, and Kenyan football history was never properly archived. If you know of a first, a title or a landmark reached by a Kenyan or a player of Kenyan descent that is not listed here, tell us."
            : "This archive was built from public records, and public records on Kenyan football are thin. Players who turned professional in the 1970s, 1980s and 1990s are the most likely to be missing, and so are those in leagues the databases ignore."}
        </p>
        <p className="body-copy mt-4 max-w-3xl">
          We are asking the press, football fans, coaches, club officials, families and the players themselves to
          register anyone we have not named. Send what you know, even if it is only a name and a club — we will do
          the research, publish what we can verify, and credit you for finding it.
        </p>
        <a
          href={`mailto:${ARCHIVE_INBOX}?subject=${encodeURIComponent(
            firsts ? "A Kenyan football first you have missed" : "A Kenyan player you have missed",
          )}`}
          data-testid="archive-mailto"
          className="mono mt-7 inline-flex items-center gap-2 border border-ochre/40 px-4 py-3 text-[0.6rem] uppercase tracking-[0.16em] text-ochre transition-colors hover:bg-ochre hover:text-black"
        >
          <Mail size={13} /> {ARCHIVE_INBOX}
        </a>

        {done ? (
          <p className="mono mt-10 max-w-2xl border-l-2 border-ochre bg-black p-6 text-[0.62rem] uppercase leading-relaxed tracking-[0.14em] text-ochre" data-testid="archive-done">
            Received. It is in the research queue and you will hear from us at the address you gave.
          </p>
        ) : (
          <form onSubmit={submit} className="mt-10 max-w-4xl" data-testid="archive-form">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Player's name" name="player_name" value={form.player_name} onChange={set} required placeholder="Full name" />
              <Field label="Years playing" name="years" value={form.years} onChange={set} placeholder="e.g. 1987 to 1996" />
              <Field label="Clubs" name="clubs" value={form.clubs} onChange={set} placeholder="Clubs, in order if you know them" span="sm:col-span-2" />
              <Field label="Country or league" name="country" value={form.country} onChange={set} placeholder="e.g. Sweden, Allsvenskan" />
              <Field
                label={firsts ? "The first or landmark" : "What they are known for"}
                name="achievement"
                value={form.achievement}
                onChange={set}
                placeholder={firsts ? "e.g. first Kenyan to play in Portugal" : "Caps, titles, a notable transfer"}
              />
              <Field label="Anything we can check it against" name="evidence" value={form.evidence} onChange={set} placeholder="A link, a newspaper, a name we can call" span="sm:col-span-2" />
              <Field label="Your name" name="your_name" value={form.your_name} onChange={set} required placeholder="So we can credit you" />
              <Field label="Your email" name="your_email" value={form.your_email} onChange={set} required placeholder="Where we reply" />
              <label className="block sm:col-span-2">
                <span className="mono block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-400">
                  You are
                </span>
                <select
                  name="your_role"
                  value={form.your_role}
                  onChange={set}
                  data-testid="archive-your-role"
                  className="mt-2 w-full border border-white/20 bg-black px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none"
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </label>
              <label className="block sm:col-span-2">
                <span className="mono block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-400">
                  Anything else we should know
                </span>
                <textarea
                  name="notes"
                  rows={4}
                  value={form.notes}
                  onChange={set}
                  data-testid="archive-notes"
                  placeholder="Where they played, who remembers them, what we should look up"
                  className="mt-2 w-full border border-white/20 bg-transparent px-3.5 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:border-ochre focus:outline-none"
                />
              </label>
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="archive-submit"
              className="btn-primary mt-7 disabled:opacity-60"
            >
              {busy ? "Sending" : firsts ? "Register this first" : "Register this player"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};
